import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TableChartIcon from '@mui/icons-material/TableChart';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';
import { useAuth } from '../context/AuthContext';
import config from '../config';

// The Express backend (subhroBackendDashboard), same as every other page —
// served out of its DuckDB database rather than the Kobo-backed
// api/dashboard/[...slug].js functions this used to call.
const API_URL = config.apiUrl;
const PAGE_LIMIT = 50;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const calcDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms)) return 'Invalid';
  const min = Math.abs(ms / 60000);
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
const formatTime = (minutes) => {
  if (!minutes) return '0 min';
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

const EMPTY_STATS = {
  totalSurveys: 0, validSurveys: 0, excludedLongSurveys: 0,
  averageTimeMinutes: 0, medianTimeMinutes: 0,
  totalHouseholdMembers: 0, averageHouseholdMembers: 0, medianHouseholdMembers: 0,
  minHouseholdMembers: 0, maxHouseholdMembers: 0,
  totalChildrenReceiving: 0, averageChildrenReceiving: 0, medianChildrenReceiving: 0,
  minChildrenReceiving: 0, maxChildrenReceiving: 0,
  longDurationCount: 0, longDurationDetails: [],
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [surveys, setSurveys] = useState([]);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [tableOpen, setTableOpen] = useState(false); // collapsed by default
  const [longOpen, setLongOpen] = useState(false);

  // Respondent drilldown drawer
  const [drawer, setDrawer] = useState({ open: false, name: '', records: [], count: 0 });

  const fetchStats = useCallback(async (filters) => {
    try {
      const params = buildParams(filters);
      const res = await axios.get(`${API_URL}/dashboard-stats?${params}`);
      if (res.data?.success) setStats({ ...EMPTY_STATS, ...res.data.data });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    }
  }, []);

  const fetchSurveys = useCallback(async (filters, page = 1) => {
    setLoading(true);
    try {
      const params = buildParams(filters, { extra: { page, limit: PAGE_LIMIT } });
      const res = await axios.get(`${API_URL}/surveys-advanced?${params}`);
      if (res.data?.success) {
        setSurveys(res.data.surveys || []);
        setPagination(normalizePagination({
          page: res.data.page,
          total: res.data.total,
          totalPages: res.data.totalPages,
          limit: PAGE_LIMIT,
        }));
      }
    } catch (err) {
      console.error('Error fetching surveys:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(applied);
    fetchSurveys(applied, 1);
  }, [applied, fetchStats, fetchSurveys]);

  const handleRespondentClick = async (name) => {
    if (!name || name === 'N/A') return;
    try {
      const params = buildParams(applied, { extra: { respondentName: name, limit: 999999 } });
      const res = await axios.get(`${API_URL}/surveys-by-respondent?${params}`);
      if (res.data?.success) {
        setDrawer({
          open: true,
          name,
          records: res.data.records || [],
          count: res.data.count ?? (res.data.records || []).length,
        });
      }
    } catch (err) {
      console.error('Error fetching respondent records:', err);
    }
  };

  const columns = [
    { key: 'idx', label: '#', render: (_r, i) => (pagination.page - 1) * PAGE_LIMIT + i + 1 },
    { key: 'start', label: 'Start', render: (r) => formatDate(r.start) },
    { key: 'end', label: 'End', render: (r) => formatDate(r.end) },
    { key: 'dur', label: 'Duration', render: (r) => <span className="cell-strong">{calcDuration(r.start, r.end)}</span> },
    { key: 'enumerator_id', label: 'Enumerator', render: (r) => r.enumerator_id || 'N/A' },
    { key: 'block', label: 'Block', render: (r) => r.block || 'N/A' },
    { key: 'gram_panchayat', label: 'GP', render: (r) => r.gram_panchayat || 'N/A' },
    { key: 'village_name', label: 'Village', render: (r) => r.village_name || 'N/A' },
    { key: 'Hamlet', label: 'Hamlet', render: (r) => r.Hamlet || 'N/A' },
    {
      key: 'respondent_name', label: 'Respondent',
      render: (r) => (
        <span className="clickable-name" onClick={(e) => { e.stopPropagation(); handleRespondentClick(r.respondent_name); }}>
          {r.respondent_name || 'N/A'}
        </span>
      ),
    },
    ...(user?.role === 'admin'
      ? [{ key: 'respondent_phone', label: 'Phone', render: (r) => r.respondent_phone || 'N/A' }]
      : []),
    { key: 'total_household_member', label: 'Members', align: 'center', render: (r) => r.total_household_member || 0 },
    { key: 'Nos_of_children_receiving', label: 'Children', align: 'center', render: (r) => r.Nos_of_children_receiving || 0 },
  ];

  const drawerRecordColumns = [
    { key: 'idx', label: '#', render: (_r, i) => i + 1 },
    { key: 'start', label: 'Start', render: (r) => formatDate(r.start) },
    { key: 'dur', label: 'Duration', render: (r) => calcDuration(r.start, r.end) },
    { key: 'enumerator_id', label: 'Enumerator', render: (r) => r.enumerator_id || 'N/A' },
    { key: 'block', label: 'Block', render: (r) => r.block || 'N/A' },
    { key: 'village_name', label: 'Village', render: (r) => r.village_name || 'N/A' },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <DashboardIcon /> Dashboard
        </h1>
        <p className="page-subtitle">Survey KPIs and records overview.</p>
      </div>

      <FilterBar
        apiBase={API_URL}
        onApply={(v) => setApplied(v)}
        onClear={() => setApplied(EMPTY)}
        loading={loading}
      />

      {/* Time stats */}
      <div className="stat-tile-row">
        <StatTile label="Total Surveys" value={stats.totalSurveys} icon={TableChartIcon} accent="var(--primary)" />
        <StatTile label="Valid Surveys" value={stats.validSurveys} icon={AccessTimeIcon} accent="var(--success)" sub="≤ 1 hour" />
        <StatTile label="Excluded" value={stats.excludedLongSurveys} icon={WarningAmberIcon} accent="var(--warning)" sub="> 1 hour" />
        <StatTile label="Average Time" value={formatTime(stats.averageTimeMinutes)} icon={AccessTimeIcon} accent="var(--info)" />
        <StatTile label="Median Time" value={formatTime(stats.medianTimeMinutes)} icon={AccessTimeIcon} accent="var(--info)" />
      </div>

      {/* Household */}
      <div className="stat-tile-row">
        <StatTile label="Total Members" value={stats.totalHouseholdMembers} icon={GroupsIcon} accent="var(--cat-gender)" />
        <StatTile label="Avg Members" value={Number(stats.averageHouseholdMembers || 0).toFixed(1)} icon={GroupsIcon} accent="var(--cat-gender)" />
        <StatTile label="Median Members" value={stats.medianHouseholdMembers} icon={GroupsIcon} accent="var(--cat-gender)" />
        <StatTile label="Members Range" value={`${stats.minHouseholdMembers} – ${stats.maxHouseholdMembers}`} icon={GroupsIcon} accent="var(--cat-gender)" />
      </div>

      {/* Children */}
      <div className="stat-tile-row">
        <StatTile label="Total Children" value={stats.totalChildrenReceiving} icon={EscalatorWarningIcon} accent="var(--cat-education)" sub="Receiving scholarship" />
        <StatTile label="Avg Children" value={Number(stats.averageChildrenReceiving || 0).toFixed(1)} icon={EscalatorWarningIcon} accent="var(--cat-education)" />
        <StatTile label="Median Children" value={stats.medianChildrenReceiving} icon={EscalatorWarningIcon} accent="var(--cat-education)" />
        <StatTile label="Children Range" value={`${stats.minChildrenReceiving} – ${stats.maxChildrenReceiving}`} icon={EscalatorWarningIcon} accent="var(--cat-education)" />
      </div>

      {/* Long-duration excluded (collapsible) */}
      {stats.longDurationCount > 0 && (
        <div className="card">
          <div className="collapsible-head" onClick={() => setLongOpen((o) => !o)}>
            <h3 className="card-title" style={{ margin: 0, color: 'var(--warning)' }}>
              <WarningAmberIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {stats.longDurationCount} survey(s) excluded (duration &gt; 1 hour)
            </h3>
            {longOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
          {longOpen && (
            <div style={{ marginTop: '1rem' }}>
              <DataTable
                columns={[
                  { key: 'idx', label: '#', render: (_r, i) => i + 1 },
                  { key: 'block', label: 'Block', render: (r) => r.block || 'N/A' },
                  { key: 'gram_panchayat', label: 'GP', render: (r) => r.gram_panchayat || 'N/A' },
                  { key: 'village_name', label: 'Village', render: (r) => r.village_name || 'N/A' },
                  {
                    key: 'respondent_name', label: 'Respondent',
                    render: (r) => (
                      <span className="clickable-name" onClick={() => handleRespondentClick(r.respondent_name)}>
                        {r.respondent_name || 'N/A'}
                      </span>
                    ),
                  },
                  { key: 'dur', label: 'Duration', render: (r) => <span className="badge badge-warning">{formatTime(r.durationMinutes)}</span> },
                ]}
                rows={stats.longDurationDetails}
                getRowKey={(_r, i) => i}
              />
            </div>
          )}
        </div>
      )}

      {/* Records table (collapsible, collapsed by default) */}
      <div className="card">
        <div className="collapsible-head" onClick={() => setTableOpen((o) => !o)}>
          <h3 className="card-title" style={{ margin: 0 }}>
            <TableChartIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Survey Records ({pagination.total} total)
          </h3>
          {tableOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
        {tableOpen && (
          <div style={{ marginTop: '1rem' }}>
            <Pagination
              pagination={pagination}
              onPageChange={(p) => fetchSurveys(applied, p)}
              showPageSize={false}
              unitLabel="records"
              shown={surveys.length}
            />
            <DataTable
              columns={columns}
              rows={surveys}
              loading={loading}
              getRowKey={(r, i) => r._uuid || r._id || i}
            />
          </div>
        )}
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={`Respondent: ${drawer.name}`}
        sections={[{ title: 'Summary', items: [{ label: 'Total Records', value: drawer.count }] }]}
      >
        <div className="detail-section">
          <h4>Records</h4>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            emptyMessage="No records."
            getRowKey={(r, i) => r._uuid || r._id || i}
          />
        </div>
      </DetailDrawer>
    </div>
  );
}
